import React, { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Grid,
  IconButton,
  Alert,
  Stepper,
  Step,
  StepLabel,
} from "@mui/material";
import { Add as AddIcon, Delete as DeleteIcon, UploadFile, ArrowForward } from "@mui/icons-material";
import { Trans } from "react-i18next";
import AppContext from "../../contexts/appContext";
import ApiServices from "../../services";
import Utils from "../../services/utils";
import handleError from "../../services/handleError";
import PDFEditor from "./PDFEditor";

const CreateApproval = () => {
  const navigate = useNavigate();
  const {
    state: { auth },
    dispatch,
  } = useContext(AppContext);

  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    file: null,
    assignees: [
      {
        name: "",
        email: "",
      },
    ],
    signatureFields: [],
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const steps = [
    "Basic Information",
    "Signature Positioning"
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAssigneeChange = (index, field, value) => {
    const newAssignees = [...formData.assignees];
    newAssignees[index][field] = value;
    setFormData(prev => ({
      ...prev,
      assignees: newAssignees
    }));
  };

  const addAssignee = () => {
    setFormData(prev => ({
      ...prev,
      assignees: [...prev.assignees, { name: "", email: "" }]
    }));
  };

  const removeAssignee = (index) => {
    if (formData.assignees.length > 1) {
      const newAssignees = formData.assignees.filter((_, i) => i !== index);
      setFormData(prev => ({
        ...prev,
        assignees: newAssignees
      }));
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file && file.type === "application/pdf") {
      setError("");
      setFormData(prev => ({
        ...prev,
        file: file
      }));
    } else {
      setError("Please select a valid PDF file");
      setFormData(prev => ({
        ...prev,
        file: null
      }));
    }
  };

  // Helper function to format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleNext = () => {
    if (currentStep === 1) {
      // Validate step 1
      if (!formData.title || !formData.file) {
        setError("Please fill in all required fields");
        return;
      }
      if (formData.assignees.some(assignee => !assignee.name || !assignee.email)) {
        setError("Please fill in all assignee information");
        return;
      }
      setError("");
      setCurrentStep(2);
    }
  };

  const handleBack = () => {
    setCurrentStep(currentStep - 1);
  };

  const handleSubmit = async (signatureFields) => {
    if (!formData.title || !formData.file) {
      setError("Please fill in all required fields");
      return;
    }

    if (formData.assignees.some(assignee => !assignee.name || !assignee.email)) {
      setError("Please fill in all assignee information");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const base64File = await Utils.getBase64(formData.file);
      
      // Map signature fields to assignees based on user names
      const assigneeWithFields = formData.assignees.map(assignee => {
        // Find signature fields for this assignee
        const userFields = signatureFields.filter(field => field.user === assignee.name);
        
        return {
          name: assignee.name,
          prefix: "", // Add prefix field as expected by backend
          email: assignee.email,
          fields: userFields.map(field => ({
            type: "signature",
            id: field.id,
            page: field.page,
            x: field.x,
            y: field.y,
            width: field.width,
            height: field.height,
            user: field.user
          }))
        };
      });
      
      const response = await ApiServices.createApproval({
        token: auth.token,
        data: {
          title: formData.title,
          description: formData.description || "",
          document: base64File,
          document_name: formData.file.name,
          valid_from: null,
          valid_to: null,
          ref_id: null,
          committee: null,
          send_emails: false,
          assignee: assigneeWithFields,
          access_code: false,
          one_by_one: false
        },
      });

      if (response.success) {
        navigate("/app/approvals");
      } else {
        handleError(response.code, dispatch);
      }
    } catch (err) {
      console.error("Error creating approval:", err);
      setError("Failed to create approval. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate("/app/approvals");
  };

  // Step 1: Basic Information Form
  const renderStep1 = () => (
    <Grid container spacing={3}>
      {/* Title */}
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Title"
          value={formData.title}
          onChange={(e) => handleInputChange("title", e.target.value)}
          required
        />
      </Grid>

      {/* Description */}
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Description"
          multiline
          rows={4}
          value={formData.description}
          onChange={(e) => handleInputChange("description", e.target.value)}
        />
      </Grid>

      {/* File Upload */}
      <Grid item xs={12}>
        <Box sx={{ border: "2px dashed #ccc", p: 3, textAlign: "center" }}>
          {formData.file ? (
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
              <Box>
                <Typography variant="body1">{formData.file.name}</Typography>
                <Typography variant="body2" color="textSecondary">
                  {formatFileSize(formData.file.size)}
                </Typography>
              </Box>
              <IconButton
                color="error"
                onClick={() => setFormData(prev => ({ ...prev, file: null }))}
              >
                <DeleteIcon />
              </IconButton>
            </Box>
          ) : (
            <Button
              component="label"
              variant="outlined"
              startIcon={<UploadFile />}
            >
              Upload PDF File *
              <input
                type="file"
                hidden
                accept=".pdf"
                onChange={handleFileChange}
              />
            </Button>
          )}
        </Box>
      </Grid>

      {/* Assignees */}
      <Grid item xs={12}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
          <Typography variant="h6">Assignees *</Typography>
          <Button
            startIcon={<AddIcon />}
            onClick={addAssignee}
            variant="outlined"
            size="small"
          >
            Add Assignee
          </Button>
        </Box>

        {formData.assignees.map((assignee, index) => (
          <Box key={index} sx={{ display: "flex", gap: 2, mb: 2, alignItems: "center" }}>
            <TextField
              label="Name"
              value={assignee.name}
              onChange={(e) => handleAssigneeChange(index, "name", e.target.value)}
              sx={{ flex: 1 }}
              required
            />
            <TextField
              label="Email"
              type="email"
              value={assignee.email}
              onChange={(e) => handleAssigneeChange(index, "email", e.target.value)}
              sx={{ flex: 1 }}
              required
            />
            {formData.assignees.length > 1 && (
              <IconButton
                color="error"
                onClick={() => removeAssignee(index)}
              >
                <DeleteIcon />
              </IconButton>
            )}
          </Box>
        ))}
      </Grid>
    </Grid>
  );

  // Step 2: PDF Display with Signature Positioning
  const renderStep2 = () => (
    <Box sx={{ width: "100%" }}>
      <PDFEditor
        file={formData.file}
        onBack={handleBack}
        onNext={handleSubmit}
        loading={loading}
        assignees={formData.assignees}
        approvalMode={true} // Flag to indicate this is for approvals (only signature fields)
      />
    </Box>
  );

  return (
    <Box sx={{ p: 3, maxWidth: "100%", mx: "auto" }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Create New
      </Typography>
      <Paper elevation={2} sx={{ p: 4, mt: 2 }}>
        {/* Stepper */}
        <Box sx={{ mb: 4 }}>
          <Stepper activeStep={currentStep - 1} alternativeLabel>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Step Content */}
        {currentStep === 1 ? renderStep1() : renderStep2()}

        {/* Action Buttons - Only show for Step 1 */}
        {currentStep === 1 && (
          <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end", mt: 4 }}>
            <Button
              variant="outlined"
              onClick={handleCancel}
              disabled={loading}
            >
              <Trans>close</Trans>
            </Button>
            
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={!formData.title || !formData.file}
              endIcon={<ArrowForward />}
            >
              Next
            </Button>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default CreateApproval;
